import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { IdentityStatusEnum } from "@/lib/enum/identity-status.enum";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { handleError } from "@/lib/utility/handleError";
import { PartnerUserGoToHelper } from "@/lib/utility/partner-user.go-to-helper";
import authService from "@/services/mrfiktiv/services/authService";
import {
  MrfiktivContactDtoGen,
  MrfiktivCreateUserDtoGen,
  MrfiktivFleetPermissionDtoGen,
  MrfiktivIdentityInformationViewModelGen,
  MrfiktivNotificationConfigDtoGen,
  MrfiktivPartnerPermissionDtoGen,
  MrfiktivPartnerUserViewModelGen,
  MrfiktivPermissionDtoGen,
  MrfiktivUpdateUserDtoGen,
  MrfiktivUserAvatarViewModelGen,
  MrfiktivUserViewmodelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import partnerFleetUserService from "@/services/shared/partnerFleetUserService";
import partnerUserService from "@/services/shared/partnerUserService";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { AuthRolesEnum } from "@/store/enum/authRolesEnum";
import { AdminUserDataAccessLayer } from "@/store/modules/access-layers/admin-user.access-layer";
import { PartnerFleetUserAccessLayer } from "@/store/modules/access-layers/partner-fleet-user.access-layer";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import VueRouter from "vue-router";
import adminUserService from "../services/shared/adminUserService";
import { Address, IAddress } from "./address.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { Contact, IContact } from "./user-contact.entity";

class AuthInfo {
  username = "";
}

export type PermissionType = MrfiktivPermissionDtoGen | MrfiktivFleetPermissionDtoGen | MrfiktivPartnerPermissionDtoGen;

@IsFilterable
class Permission implements MrfiktivFleetPermissionDtoGen {
  @FilterConfig({
    displayName: "Berechtigung",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(BackendResourceEnum).map((e) => {
        return {
          text: `enums.BackendResourceEnum.${e}`,
          value: e
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  type: BackendResourceEnum;

  @FilterConfig({
    displayName: "Aktion",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(ActionEnum).map((e) => {
        return {
          text: `enums.ActionEnum.${e}`,
          value: e
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  action?: ActionEnum[] | undefined;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "views.fleet.FleetVehicleListView.groupId"
    // config: {
    //   itemCallback: () => FleetModule.paginationList,
    //   mapItemToComponent: item => ({ item }),
    //   itemValue: "id",
    //   component: "refs-fleet",
    //  refType: BackendResourceEnum.FLEET
    // }
  })
  fleetId: string;

  constructor(p: MrfiktivFleetPermissionDtoGen) {
    this.action = p.action as ActionEnum[];
    this.type = p.type as BackendResourceEnum;
    this.fleetId = p.fleetId;
    this.partnerId = p.partnerId;
  }
}

const PermissionFilter = Filter.createForClass(Permission);

@IsFilterable
class UserBase implements MrfiktivUserViewmodelGen, Partial<MrfiktivIdentityInformationViewModelGen> {
  /** The users first name */
  @FilterConfig({
    displayName: "objects.user.firstName",
    type: FilterTypes.STRING
  })
  firstName: string;

  /** The users last name */
  @FilterConfig({
    displayName: "objects.user.lastName",
    type: FilterTypes.STRING
  })
  lastName: string;

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  /** The users contact details */
  @FilterConfig({ type: Contact })
  contact: IContact;

  /** The id of the user */
  id: string;

  @FilterConfig({
    displayName: "objects.user.userName",
    type: FilterTypes.STRING
  })
  userName: string;

  /** The users profile picture in different sizes, undefined if the user has none */
  avatar?: MrfiktivUserAvatarViewModelGen;

  /**
   * The country code for the user (where the user signed up from)
   * @example DE
   */
  countryCode?: CountryCodeEnum;

  /** The users address */
  @FilterConfig({ type: Address })
  address: IAddress;

  /** Marketing OptIn of the User */
  isMarketingOptIn: boolean;

  /** The User Accepted the AGBS */
  isTermsAccepted: boolean;

  /** The Companyname */
  @FilterConfig({ displayName: "objects.company.companyName", type: FilterTypes.STRING })
  company: string;

  @FilterConfig({
    displayName: "objects.user.language",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(LanguageCodeEnum).map((v) => {
        return {
          text: `enums.LanguageCodeEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  language: LanguageCodeEnum;

  timestamps: ITimestamp;

  /** The id of the user */
  @FilterConfig({ displayName: "objects.id", type: FilterTypes.OBJECT_ID })
  get _id() {
    return this.id;
  }

  authInfo: AuthInfo;

  @FilterConfig({
    displayName: "objects.user.roles",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(AuthRolesEnum).map((v) => {
        return {
          text: `enums.AuthRolesEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  roles: AuthRolesEnum[];

  @FilterConfig({ type: PermissionFilter })
  permission: PermissionType[];

  get resourcePermission(): MrfiktivPermissionDtoGen[] {
    return this.permission.filter((p): p is MrfiktivPermissionDtoGen => "id" in p);
  }

  get fleetPermission(): MrfiktivFleetPermissionDtoGen[] {
    return this.permission.filter((p): p is MrfiktivFleetPermissionDtoGen => "fleetId" in p);
  }

  /**
   * Can user eligible for tax
   * @example true
   */
  isTaxDeductible?: boolean;

  /**
   * taxnumber of user
   * @example DE 123 456 78
   */
  @FilterConfig({ displayName: "objects.user.taxnumber", type: FilterTypes.STRING })
  taxnumber?: string;

  /**
   * Is user company user
   * @example true
   */
  isCompany?: boolean;

  partnerId: string;

  @FilterConfig({ displayName: "objects.user.externalId", type: FilterTypes.STRING })
  externalId?: string;

  /** Free-text description of the user role and responsibilities */
  description?: string;

  /** Whether this user can receive call transfers */
  isCallTransferEnabled?: boolean;

  status?: IdentityStatusEnum;

  notificationConfig: MrfiktivNotificationConfigDtoGen = {
    assigned: {
      isMail: false,
      isPush: false
    },
    mentioned: {
      isMail: false,
      isPush: false
    }
  };

  constructor(
    user?: Partial<MrfiktivUserViewmodelGen & MrfiktivIdentityInformationViewModelGen & { partnerId: string }>
  ) {
    this.id = user?._id ?? user?.id ?? "";
    this.address = new Address(user?.address);
    this.contact = new Contact(user?.contact);
    this.firstName = user?.firstName ?? "";
    this.lastName = user?.lastName ?? "";
    this.userName = user?.userName ?? "";
    this.avatar = user?.avatar;
    this.countryCode = user?.countryCode as CountryCodeEnum;
    this.isMarketingOptIn = user?.isMarketingOptIn ?? false;
    this.isTermsAccepted = user?.isTermsAccepted ?? false;
    this.company = user?.company ?? "";
    this.timestamps = new Timestamp(user?.timestamps);
    this.authInfo = (user?.authInfo ?? {}) as AuthInfo;
    this.roles = (user?.roles ?? []) as AuthRolesEnum[];
    this.permission = user?.permission ?? [];
    this.isTaxDeductible = user?.isTaxDeductible;
    this.taxnumber = user?.taxnumber;
    this.isCompany = user?.isCompany;
    this.partnerId = user?.partnerId ?? "";
    this.status = user?.status as IdentityStatusEnum;
    if (user?.notificationConfig) {
      this.notificationConfig = user?.notificationConfig;
    }
    this.language = user?.language as LanguageCodeEnum;
    this.externalId = user?.externalId;
  }

  map(user?: MrfiktivUserViewmodelGen) {
    this.id = user?._id ?? user?.id ?? "";
    this.address = new Address(user?.address);
    this.contact = new Contact(user?.contact);
    this.firstName = user?.firstName ?? "";
    this.lastName = user?.lastName ?? "";
    this.userName = user?.userName ?? "";
    this.avatar = user?.avatar;
    this.countryCode = user?.countryCode as CountryCodeEnum;
    this.isMarketingOptIn = user?.isMarketingOptIn ?? false;
    this.isTermsAccepted = user?.isTermsAccepted ?? false;
    this.company = user?.company ?? "";
    this.timestamps = new Timestamp(user?.timestamps);
    this.authInfo = (user?.authInfo ?? {}) as AuthInfo;
    this.roles = (user?.roles ?? []) as AuthRolesEnum[];
    this.permission = user?.permission ?? [];
    this.isTaxDeductible = user?.isTaxDeductible;
    this.taxnumber = user?.taxnumber;
    this.isCompany = user?.isCompany;
    if (user?.notificationConfig) {
      this.notificationConfig = user?.notificationConfig;
    }
    this.language = user?.language as LanguageCodeEnum;
    this.externalId = user?.externalId;
    this.userName = user?.userName ?? "";
  }

  mapIdentity(user?: MrfiktivIdentityInformationViewModelGen) {
    this.status = user?.status as IdentityStatusEnum;
  }
}

@IsFilterable
class PartnerUserBase
  extends UserBase
  implements IEntity<MrfiktivPartnerUserViewModelGen & Partial<MrfiktivIdentityInformationViewModelGen>>
{
  partnerId: string;

  constructor(
    user?: Partial<MrfiktivPartnerUserViewModelGen & Partial<MrfiktivIdentityInformationViewModelGen>> & {
      partnerId: string;
    }
  ) {
    super(user);

    this.partnerId = user?.partnerId ?? "";
    this.description = user?.description;
    this.isCallTransferEnabled = user?.isCallTransferEnabled;

    // Mail notifications default to on for a newly invited user, so they don't miss activity on
    // their own documents from day one. Only applies when nothing was passed in - a user built from
    // a real fetch response already carries its actual notificationConfig via UserBase's constructor.
    if (!user?.notificationConfig) {
      this.notificationConfig = {
        assigned: { isMail: true, isPush: false },
        mentioned: { isMail: true, isPush: false }
      };
    }
  }

  map(user?: MrfiktivUserViewmodelGen | MrfiktivPartnerUserViewModelGen) {
    if (!user) {
      return;
    }

    super.map(user as MrfiktivUserViewmodelGen);

    if ((user as MrfiktivPartnerUserViewModelGen).partnerId) {
      this.partnerId = (user as MrfiktivPartnerUserViewModelGen).partnerId;
      this.description = (user as MrfiktivPartnerUserViewModelGen)?.description;
      this.isCallTransferEnabled = (user as MrfiktivPartnerUserViewModelGen)?.isCallTransferEnabled;
    }

    this.userName = user?.userName ?? "";
  }

  async fetch(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("partnerId is missing");
    }

    const user = await partnerUserService.findOne(this.partnerId, this.id);

    this.map(user);

    PartnerUserAccessLayer.set(this);

    return this;
  }

  async fetchStatus(): Promise<this> {
    try {
      if (!this.partnerId) {
        throw new Error("partnerId is missing");
      }

      const { user, identity } = await partnerUserService.getStatus(this.partnerId, this.id);

      this.map(user);
      this.mapIdentity(identity);

      PartnerUserAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  async create(): Promise<this> {
    try {
      if (!this.partnerId) {
        throw new Error("partnerId is missing");
      }

      const user = await partnerUserService.create(this.partnerId, {
        firstName: this.firstName,
        lastName: this.lastName,
        userName: this.userName,
        address: this.address,
        company: this.company,
        contact: this.contact.dto,
        countryCode: this.countryCode,
        isCompany: this.isCompany,
        isMarketingOptIn: this.isMarketingOptIn,
        isTaxDeductible: this.isTaxDeductible,
        isTermsAccepted: this.isTermsAccepted,
        taxnumber: this.taxnumber
      });

      this.map(user);

      PartnerUserAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  async delete(): Promise<void> {
    try {
      if (!this.partnerId) {
        throw new Error("partnerId is missing");
      }

      const user = await partnerUserService.remove(this.partnerId, this.id);

      this.map(user);

      PartnerUserAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }
  }

  async update(): Promise<this> {
    try {
      const user = await partnerUserService.update(this.partnerId, this.id, {
        firstName: this.firstName,
        lastName: this.lastName,
        externalId: this.externalId,
        language: this.language,
        contact: this.contact.dto,
        address: this.address,
        isCompany: this.isCompany,
        company: this.company,
        isTaxDeductible: this.isTaxDeductible,
        taxnumber: this.taxnumber,
        description: this.description,
        isCallTransferEnabled: this.isCallTransferEnabled
      });

      this.map(user);

      PartnerUserAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  /**
   * Left uncaught, unlike most other methods here, so a failed update actually rejects instead of
   * resolving as if it had saved - callers already show their own success/error toast on this.
   */
  async updateNotificationConfig(notificationConfig: MrfiktivNotificationConfigDtoGen): Promise<this> {
    const user = await partnerUserService.update(this.partnerId, this.id, { notificationConfig });

    this.map(user);

    PartnerUserAccessLayer.set(this);

    return this;
  }

  /**
   * Replaces the profile picture of the user. Errors are left to the caller, so it can tell success from failure.
   */
  async uploadAvatar(image: File): Promise<this> {
    const user = await partnerUserService.uploadAvatar(this.partnerId, this.id, image);

    this.map(user);

    PartnerUserAccessLayer.set(this);

    return this;
  }

  /**
   * Drops the profile picture of the user. Errors are left to the caller, so it can tell success from failure.
   */
  async removeAvatar(): Promise<this> {
    const user = await partnerUserService.removeAvatar(this.partnerId, this.id);

    this.map(user);

    PartnerUserAccessLayer.set(this);

    return this;
  }

  async resendInvitation(silent?: boolean): Promise<this> {
    try {
      if (!this.partnerId) {
        throw new Error("partnerId is missing");
      }

      const user = await partnerUserService.resendInvitation(this.partnerId, this.id);

      this.map(user);

      PartnerUserAccessLayer.set(this);
    } catch (error) {
      handleError(error);
      if (!silent) throw error;
    }

    return this;
  }

  get dto(): MrfiktivCreateUserDtoGen {
    const dto: MrfiktivCreateUserDtoGen = {
      firstName: this.firstName,
      lastName: this.lastName,
      userName: this.userName,
      address: this.address,
      company: this.company,
      contact: this.contact.dto,
      countryCode: this.countryCode,
      isCompany: this.isCompany,
      isMarketingOptIn: this.isMarketingOptIn,
      isTaxDeductible: this.isTaxDeductible,
      isTermsAccepted: this.isTermsAccepted,
      taxnumber: this.taxnumber,
      notificationConfig: this.notificationConfig
    };

    if (!(this.address.city && this.address.street && this.address.zip && this.address.state)) {
      dto.address = undefined;
    }

    if (!this.contact?.email) {
      if (!this.contact) {
        dto.contact = { email: this.userName };
      } else {
        (dto.contact as MrfiktivContactDtoGen).email = this.userName;
      }
    }

    return dto;
  }

  get isAdmin() {
    return this.roles?.includes(AuthRolesEnum.ADMIN);
  }

  @FilterConfig({
    displayName: "objects.user.isArchived",
    type: FilterTypes.BOOLEAN
  })
  get isArchived() {
    if (this.isAdmin) {
      return false;
    }

    const partnerPermissions = this.permission.filter(
      (permission) => "id" in permission && permission.id === this.partnerId
    );

    /**
     * user is archived if the only permission for the partner is PARTNER:read
     */
    if (partnerPermissions?.length === 1 && partnerPermissions[0].type === ResourceEnum.PARTNER) {
      return true;
    }

    return false;
  }

  /**
   * Remove permissions from user.
   * Partner Permissions must be removed via "delete" route
   * @param permissions
   * @returns
   */
  async removePermission(permissions: PermissionType[]) {
    try {
      permissions = permissions.filter((p) => p.type !== ResourceEnum.PARTNER);

      const partnerPermission: MrfiktivPermissionDtoGen[] = permissions.filter(
        (p): p is MrfiktivPermissionDtoGen => "id" in p
      );

      const updatedUser = await partnerUserService.removePermission(this.partnerId, this.id, partnerPermission);

      this.map(updatedUser);
      PartnerUserAccessLayer.set(this);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  /**
   * @param permissions the permissions to add
   * @param silent if false the error is rethrown after it is handled, so the caller can react to a failed request.
   * Defaults to true to keep the behavior of callers that do not handle errors themselves.
   */
  async addPermissions(permissions: PermissionType[], silent = true) {
    try {
      const partnerPermission: MrfiktivPermissionDtoGen[] = permissions.filter(
        (p): p is MrfiktivPermissionDtoGen => "id" in p
      );

      const updatedUser = await partnerUserService.addPermissions(this.partnerId, this.id, partnerPermission);

      this.map(updatedUser);
      PartnerUserAccessLayer.set(this);
    } catch (error) {
      handleError(error);
      if (!silent) throw error;
    }

    return this;
  }

  async invite() {
    try {
      const user = await partnerUserService.invite(this.partnerId, this.dto);
      this.map(user);
      PartnerUserAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  goTo(router: VueRouter) {
    return {
      table: () =>
        new PartnerUserGoToHelper(router).goToPartnerUserTable({
          partnerId: this.partnerId
        }),
      detail: () =>
        new PartnerUserGoToHelper(router).goToPartnerUserDetail({
          partnerId: this.partnerId,
          partnerUserId: this.id
        }),
      form: () =>
        new PartnerUserGoToHelper(router).goToPartnerUserForm({
          partnerId: this.partnerId,
          partnerUserId: this.id
        })
    };
  }
}

class PartnerFleetUserBase extends PartnerUserBase {
  fleetId: string;

  constructor(
    user?: Partial<MrfiktivPartnerUserViewModelGen & Partial<MrfiktivIdentityInformationViewModelGen>> & {
      partnerId: string;
      fleetId: string;
    }
  ) {
    super(user);

    this.partnerId = user?.partnerId ?? "";
    this.fleetId = user?.fleetId ?? "";
  }

  async fetch(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("partnerId is missing");
    }
    if (!this.fleetId) {
      throw new Error("fleetId is missing");
    }

    const user = await partnerFleetUserService.findOne(this.partnerId, this.fleetId, this.id);

    this.map(user);

    PartnerFleetUserAccessLayer.set(this);

    return this;
  }

  async fetchStatus(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("partnerId is missing");
    }

    if (!this.fleetId) {
      throw new Error("partnerId is missing");
    }

    const { user, identity } = await partnerFleetUserService.getStatus(this.partnerId, this.fleetId, this.id);

    this.map(user);
    this.mapIdentity(identity);

    PartnerFleetUserAccessLayer.set(this);

    return this;
  }

  /**
   * Remove permissions from user.
   * Partner Permissions must be removed via "delete" route
   * @param permissions
   * @returns
   */
  async removePermission(permissions: MrfiktivFleetPermissionDtoGen[]) {
    const updatedUser = await partnerFleetUserService.removePermission(
      this.partnerId,
      this.fleetId,
      this.id,
      permissions
    );

    this.map(updatedUser);
    PartnerFleetUserAccessLayer.set(this);

    return this;
  }

  async addPermissions(permissions: MrfiktivFleetPermissionDtoGen[]) {
    const updatedUser = await partnerFleetUserService.addPermissions(
      this.partnerId,
      this.fleetId,
      this.id,
      permissions
    );

    this.map(updatedUser);
    PartnerFleetUserAccessLayer.set(this);

    return this;
  }

  async invite() {
    const user = await partnerFleetUserService.invite(this.partnerId, this.fleetId, this.dto);
    this.map(user);
    PartnerUserAccessLayer.set(this);

    return this;
  }
}

class AdminUserBase
  extends UserBase
  implements IEntity<MrfiktivUserViewmodelGen & Partial<MrfiktivIdentityInformationViewModelGen>>
{
  constructor(
    user?:
      | Partial<MrfiktivUserViewmodelGen & Partial<MrfiktivIdentityInformationViewModelGen>>
      | MrfiktivCreateUserDtoGen
  ) {
    super(user);

    // Mail notifications default to on for a newly invited admin, so they don't miss activity on
    // their own documents from day one. Only applies when nothing was passed in - a user built from
    // a real fetch response already carries its actual notificationConfig via UserBase's constructor.
    if (!user?.notificationConfig) {
      this.notificationConfig = {
        assigned: { isMail: true, isPush: false },
        mentioned: { isMail: true, isPush: false }
      };
    }
  }

  async fetch(): Promise<this> {
    try {
      const user = await adminUserService.get(this.id);

      this.map(user);
      AdminUserDataAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  async removePermission(permissions: MrfiktivPermissionDtoGen[]) {
    try {
      const updatedUser = await adminUserService.removePermission(this.id, permissions);

      this.map(updatedUser);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  /**
   * @param permissions the permissions to add
   * @param silent if false the error is rethrown after it is handled, so the caller can react to a failed request.
   * Defaults to true to keep the behavior of callers that do not handle errors themselves.
   */
  async addPermissions(permissions: MrfiktivPermissionDtoGen[], silent = true) {
    try {
      const updatedUser = await adminUserService.addPermissions(this.id, permissions);

      this.map(updatedUser);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
      if (!silent) throw error;
    }

    return this;
  }

  async updateRoles() {
    try {
      const updatedUser = await adminUserService.updateRole(this.id, this.roles);
      this.map(updatedUser);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  async update() {
    try {
      const updatedUser = await adminUserService.update(this.id, {
        firstName: this.firstName,
        lastName: this.lastName,
        address: this.address,
        contact: this.contact.dto,
        isMarketingOptIn: this.isMarketingOptIn,
        company: this.company,
        isTaxDeductible: this.isTaxDeductible,
        taxnumber: this.taxnumber,
        isCompany: this.isCompany,
        language: this.language
      });

      this.map(updatedUser);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  /**
   * Left uncaught, unlike most other methods here, so a failed update actually rejects instead of
   * resolving as if it had saved - callers already show their own success/error toast on this.
   */
  async updateNotificationConfig(notificationConfig: MrfiktivNotificationConfigDtoGen): Promise<this> {
    const updatedUser = await adminUserService.update(this.id, { notificationConfig });

    this.map(updatedUser);
    AdminUserDataAccessLayer.set(this);

    return this;
  }

  async updateFromDto(dto: MrfiktivUpdateUserDtoGen) {
    try {
      const updatedUser = await adminUserService.update(this.id, dto);

      this.map(updatedUser);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  async invite() {
    const createdUser = await adminUserService.invite({
      firstName: this.firstName,
      lastName: this.lastName,
      userName: this.userName,
      countryCode: this.countryCode,
      address:
        this.address.city && this.address.street && this.address.zip && this.address.countryCode
          ? this.address
          : undefined,
      contact: this.contact.dto ? this.contact.dto : undefined,
      company: this.company,
      isMarketingOptIn: this.isMarketingOptIn,
      isTermsAccepted: this.isTermsAccepted,
      isTaxDeductible: this.isTaxDeductible,
      isCompany: this.isCompany,
      taxnumber: this.taxnumber,
      notificationConfig: this.notificationConfig
    });

    this.map(createdUser);
    AdminUserDataAccessLayer.set(this);

    return this;
  }

  async resendInvitation(silent?: boolean) {
    try {
      const user = await adminUserService.resendInvitation(this.id);

      this.map(user);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
      if (!silent) throw error;
    }

    return this;
  }

  async getIdentity() {
    try {
      if (!this.userName) {
        throw new Error("userName is missing");
      }
      const identityInfo = await authService.getIdentity(this.userName);
      this.mapIdentity(identityInfo);
      AdminUserDataAccessLayer.set(this);
    } catch (error) {
      handleError(error);
    }

    return this;
  }

  /**
   * Drops the profile picture of the user. Errors are left to the caller, so it can tell success from failure.
   */
  async removeAvatar() {
    const updatedUser = await adminUserService.removeAvatarAsAdmin(this.id);

    this.map(updatedUser);
    AdminUserDataAccessLayer.set(this);

    return this;
  }

  async delete() {
    try {
      const user = await adminUserService.delete(this.id);
      this.map(user);
      AdminUserDataAccessLayer.delete(this);
    } catch (error) {
      handleError(error);
    }
  }
}

type IUser = UserBase;
const User = UserBase;
type IPartnerUser = PartnerUserBase;
const PartnerUser = Filter.createForClass(PartnerUserBase);
type IPartnerFleetUser = PartnerFleetUserBase;
const PartnerFleetUser = Filter.createForClass(PartnerFleetUserBase);
type IAdminUser = AdminUserBase;
const AdminUser = Filter.createForClass(AdminUserBase);

export { AdminUser, IAdminUser, IPartnerFleetUser, IPartnerUser, IUser, PartnerFleetUser, PartnerUser, User };
